import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useAuth } from "../context/AuthContext";
import {
  useAvailableBalances,
  useMarketAssets,
  useZar8YieldHistory,
  useZar8YieldPositions,
} from "../hooks/query/queries";
import Toaster from "../components/ui/toaster";
import InvestYieldEmptyState from "./InvestYieldEmptyState";
import InvestYieldPopulatedState from "./InvestYieldPopulatedState";
import Zar8LockFlow from "./Zar8LockFlow";
import Zar8UnlockFlow from "./Zar8UnlockFlow";
import { parseNumericValue } from "../utils/numberFormatter";
import { getYieldMarketDataBySymbol } from "../utils/zar8Yield";
import {
  clearZar8LockFlowState,
  createDefaultZar8LockFlowState,
  getZar8YieldFlowPath,
  persistZar8LockFlowState,
  readZar8LockFlowState,
  resolveZar8LockFlowState,
  ZAR8_LOCK_FLOW_STEPS,
  ZAR8_YIELD_FLOW_TYPES,
} from "../utils/zar8LockFlow";
import {
  buildZar8YieldActivePositions,
  buildZar8YieldPerformanceSeries,
  getZar8YieldTotalLockedValue,
} from "../utils/zar8YieldOverview";
import {
  getZar8YieldHistoryFromResponse,
  getZar8YieldPositionsFromResponse,
  getZar8YieldTotalLockedFromResponse,
  mapZar8YieldPositionsToLedgerEntries,
} from "../utils/zar8YieldApi";

export default function Zar8DashboardContent({
  isPreview = false,
  basePath = "/zar8/track",
  routeFlowType = null,
}) {
  const navigate = useNavigate();
  const { settings, userProfile } = useAuth();
  const userId = userProfile?._id;
  const [lockFlowState, setLockFlowState] = useState(() =>
    readZar8LockFlowState()
  );
  const resolvedFlowState = useMemo(
    () => resolveZar8LockFlowState(lockFlowState, routeFlowType),
    [lockFlowState, routeFlowType]
  );

  const { data: availableBalances } = useAvailableBalances(userId);
  const positionQueryParams = useMemo(
    () => ({
      status: "active",
      page: 1,
      limit: 100,
    }),
    []
  );
  const historyQueryParams = useMemo(
    () => ({
      page: 1,
      limit: 50,
    }),
    []
  );
  const {
    data: zar8YieldPositions,
    isError: hasYieldPositionsError,
    isLoading: isYieldPositionsLoading,
  } = useZar8YieldPositions(positionQueryParams, !!userId);
  const {
    data: zar8YieldHistory,
    isError: hasYieldHistoryError,
    isLoading: isYieldHistoryLoading,
  } = useZar8YieldHistory(historyQueryParams, !!userId);
  const { data: marketAssets } = useMarketAssets();

  const zar8Asset = useMemo(
    () =>
      availableBalances?.find(
        (asset) => asset?.asset?.toLowerCase() === "zar8"
      ) || null,
    [availableBalances]
  );

  const zar8Balance = parseNumericValue(zar8Asset?.availableAmount);
  const apiPositions = useMemo(
    () => getZar8YieldPositionsFromResponse(zar8YieldPositions),
    [zar8YieldPositions]
  );
  const activeYieldEntries = useMemo(
    () => mapZar8YieldPositionsToLedgerEntries(apiPositions),
    [apiPositions]
  );
  const yieldPositions = useMemo(
    () => buildZar8YieldActivePositions(activeYieldEntries),
    [activeYieldEntries]
  );
  const totalLockedValue = useMemo(
    () =>
      getZar8YieldTotalLockedFromResponse(zar8YieldPositions) ||
      getZar8YieldTotalLockedValue(yieldPositions),
    [yieldPositions, zar8YieldPositions]
  );
  const totalYieldValue = zar8Balance + totalLockedValue;
  const historyItems = useMemo(
    () => getZar8YieldHistoryFromResponse(zar8YieldHistory),
    [zar8YieldHistory]
  );

  const yieldMarketData = useMemo(
    () => getYieldMarketDataBySymbol(marketAssets, "ZAR8"),
    [marketAssets]
  );
  const performanceSeries = useMemo(
    () =>
      buildZar8YieldPerformanceSeries({
        performanceData: historyItems
          .slice()
          .reverse()
          .map((entry) => ({
            label: entry?.date,
            value: entry?.type === "unlocked" ? 0 : entry?.zarAmount,
          })),
      }),
    [historyItems]
  );
  const hasOverviewDataError = hasYieldPositionsError || hasYieldHistoryError;
  const displayActiveYieldEntries = activeYieldEntries;
  const displayYieldPositions = yieldPositions;
  const displayTotalYieldValue = totalYieldValue;
  const displayTotalLockedValue = totalLockedValue;
  const displayPerformanceSeries = performanceSeries;
  const displayPrimaryYieldPosition = displayYieldPositions[0] || null;
  const shouldShowPerformanceError = hasYieldHistoryError;
  const shouldShowPositionsError = hasYieldPositionsError;
  const hasDisplayYieldActivity =
    displayActiveYieldEntries.length > 0 || historyItems.length > 0;

  useEffect(() => {
    if (resolvedFlowState?.isOpen) {
      persistZar8LockFlowState(resolvedFlowState);
      return;
    }

    clearZar8LockFlowState();
  }, [resolvedFlowState]);

  const handleOpenYieldFlow = (flowType = ZAR8_YIELD_FLOW_TYPES.lock) => {
    window.scrollTo(0, 0);
    setLockFlowState((currentState) => {
      const nextState = {
        ...createDefaultZar8LockFlowState(),
        ...currentState,
        isOpen: true,
        flowType,
        step:
          currentState?.step === ZAR8_LOCK_FLOW_STEPS.success
            ? ZAR8_LOCK_FLOW_STEPS.amount
            : currentState?.step || ZAR8_LOCK_FLOW_STEPS.amount,
      };
      navigate(getZar8YieldFlowPath(basePath, flowType));
      return nextState;
    });
  };

  const handleOpenYieldCalculator = () => {
    window.scrollTo(0, 0);
    navigate(`${basePath.replace(/\/+$/, "")}/calculator`, {
      state: {
        returnTo: basePath,
      },
    });
  };

  const handleSwitchYieldFlowMode = (nextFlowType) => {
    window.scrollTo(0, 0);
    setLockFlowState({
      ...createDefaultZar8LockFlowState(),
      isOpen: true,
      flowType: nextFlowType,
    });
    navigate(getZar8YieldFlowPath(basePath, nextFlowType));
  };

  const handleCloseLockFlow = () => {
    clearZar8LockFlowState();
    setLockFlowState(createDefaultZar8LockFlowState());
    window.scrollTo(0, 0);
    navigate(basePath);
  };

  const handleUnlockPosition = (position) => {
    window.scrollTo(0, 0);
    setLockFlowState({
      ...createDefaultZar8LockFlowState(),
      isOpen: true,
      flowType: ZAR8_YIELD_FLOW_TYPES.unlock,
      step: ZAR8_LOCK_FLOW_STEPS.amount,
      selectedUnlockPositionId: position?.id || "",
    });
    navigate(getZar8YieldFlowPath(basePath, ZAR8_YIELD_FLOW_TYPES.unlock));
  };

  const handleOpenPositionPerformance = (
    position = displayPrimaryYieldPosition
  ) => {
    if (!position?.ledgerId) {
      navigate("/invest");
      return;
    }

    navigate(`/zar8/track/position/${position.ledgerId}`, {
      state: {
        positionSnapshot: {
          ...position,
          isMock: Boolean(position?.rawEntry?.isMock),
        },
        productId: position.productId,
        ledgerId: position.ledgerId,
        returnTo: basePath,
      },
    });
  };

  if (resolvedFlowState?.isOpen) {
    return (
      <main>
        <Toaster />
        {resolvedFlowState.flowType === ZAR8_YIELD_FLOW_TYPES.unlock ? (
          <Zar8UnlockFlow
            flowState={resolvedFlowState}
            onFlowStateChange={setLockFlowState}
            activeYieldEntries={displayActiveYieldEntries}
            zar8Asset={zar8Asset}
            isTwoFactorEnabled={Boolean(
              settings?.security?.twoFactorAuth?.enabled
            )}
            onClose={handleCloseLockFlow}
            onModeChange={handleSwitchYieldFlowMode}
          />
        ) : (
          <Zar8LockFlow
            flowState={resolvedFlowState}
            onFlowStateChange={setLockFlowState}
            availableBalanceZar={zar8Balance}
            zar8Asset={zar8Asset}
            isTwoFactorEnabled={Boolean(
              settings?.security?.twoFactorAuth?.enabled
            )}
            onClose={handleCloseLockFlow}
            onModeChange={handleSwitchYieldFlowMode}
          />
        )}
      </main>
    );
  }

  if (
    !hasDisplayYieldActivity &&
    displayTotalYieldValue <= 0 &&
    !hasOverviewDataError
  ) {
    return (
      <main>
        <Toaster />
        <InvestYieldEmptyState
          totalYieldValue={totalYieldValue}
          assetBalance={zar8Balance}
          asset={zar8Asset}
          assetSymbol="ZAR8"
          assetName="ZAR8"
          marketData={yieldMarketData}
          onManage={handleOpenYieldFlow}
          onStart={() => navigate("/trading/digitalWallet")}
          isPreview={isPreview}
        />
      </main>
    );
  }

  return (
    <main>
      <Toaster />
      <InvestYieldPopulatedState
        totalYieldValue={displayTotalYieldValue}
        totalLockedValue={displayTotalLockedValue}
        positions={displayYieldPositions}
        performanceSeries={displayPerformanceSeries}
        isPerformanceLoading={isYieldPositionsLoading || isYieldHistoryLoading}
        hasPerformanceError={shouldShowPerformanceError}
        hasPositionsError={shouldShowPositionsError}
        onManage={handleOpenYieldFlow}
        onCalculator={handleOpenYieldCalculator}
        onViewMore={() =>
          handleOpenPositionPerformance(displayPrimaryYieldPosition)
        }
        onUnlockPosition={handleUnlockPosition}
        onViewPositionPerformance={handleOpenPositionPerformance}
        isPreview={isPreview}
      />
    </main>
  );
}
